import { Injectable, PLATFORM_ID, REQUEST, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Request } from 'express';

export interface CookieOptions {
  expires?: Date | number;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

@Injectable({
  providedIn: 'root',
})
export class CookieService {
  private platformId = inject(PLATFORM_ID);
  private request = inject<Request>(REQUEST, { optional: true });


  /**
   * Set a cookie with the given name, value, and options
   * @param name Cookie name
   * @param value Cookie value
   * @param options Cookie options (expires, path, domain, secure, sameSite)
   */
  setCookie(name: string, value: string, options: CookieOptions = {}): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(
      value
    )}`;

    if (options.expires) {
      if (typeof options.expires === 'number') {
        const d = new Date();
        d.setTime(d.getTime() + options.expires * 24 * 60 * 60 * 1000);
        options.expires = d;
      }
      cookieString += `; expires=${options.expires.toUTCString()}`;
    }

    if (options.path) {
      cookieString += `; path=${options.path}`;
    }

    if (options.domain) {
      cookieString += `; domain=${options.domain}`;
    }

    if (options.secure) {
      cookieString += '; secure';
    }

    if (options.sameSite) {
      cookieString += `; samesite=${options.sameSite}`;
    }

    document.cookie = cookieString;
  }

  /**
   * Get a cookie by name
   * @param name Cookie name
   * @returns Cookie value or null if not found
   */
  getCookie(name: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return this.parseCookieString(document.cookie, name);
    } else if (this.request && this.request.headers) {
      return null;

      // TODO: RE-ENABLE THIS
      // THIS IS FOR SSR AUTHENTICATION CHECK
      // THIS IS DISABLED DUE TO THE HYDRATION ISSUES IN MULTIPLE PAGES

      // const cookieString = this.request.headers['cookie'] ?? '';
      // if (cookieString) {
      //   return this.parseCookieString(cookieString, name);
      // }
    }
    return null;
  }

  /**
   * Delete a cookie by name
   * @param name Cookie name
   * @param path Cookie path (must match the path used when setting the cookie)
   */
  deleteCookie(name: string, path = '/'): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.setCookie(name, '', {
      expires: new Date(0),
      path: path,
    });
  }

  /**
   * Check if a cookie exists
   * @param name Cookie name
   * @returns true if cookie exists, false otherwise
   */
  hasCookie(name: string): boolean {
    return this.getCookie(name) !== null;
  }

  /**
   * DRY: Parse a cookie string and return the value for a given name
   */
  private parseCookieString(cookieString: string, name: string): string | null {
    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = cookieString.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(
          cookie.substring(nameEQ.length, cookie.length)
        );
      }
    }
    return null;
  }
}
